import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './Icon';

export type StepperProps = {
  value?: number;
  defaultValue?: number;
  onChange?: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  style?: CSSProperties;
  'aria-label'?: string;
};

/** Stepper — increment/decrement an amount (ml, minutes, grams). */
export function Stepper({
  value,
  defaultValue = 0,
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  unit,
  size = 'md',
  style,
  ...rest
}: StepperProps) {
  const isControlled = value !== undefined;
  const [inner, setInner] = useState(defaultValue);
  const val = isControlled ? value : inner;

  const set = (next: number) => {
    const clamped = Math.min(max, Math.max(min, next));
    if (!isControlled) setInner(clamped);
    onChange?.(clamped);
  };

  const box = size === 'lg' ? 52 : size === 'sm' ? 36 : 44;
  const btn: CSSProperties = {
    width: box,
    height: box,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'var(--surface-sunken)',
    color: 'var(--text)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'background var(--duration-fast) var(--ease-standard)',
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-3)', ...style }} {...rest}>
      <button
        type="button"
        aria-label="Decrease"
        onClick={() => set(val - step)}
        disabled={val <= min}
        style={{ ...btn, opacity: val <= min ? 0.4 : 1, cursor: val <= min ? 'not-allowed' : 'pointer' }}
      >
        <Icon name="Minus" size={18} strokeWidth={2.5} />
      </button>

      <div
        aria-live="polite"
        style={{
          minWidth: box + 8,
          textAlign: 'center',
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: size === 'lg' ? 'var(--text-2xl)' : 'var(--text-xl)',
          fontWeight: 'var(--fw-medium)' as unknown as number,
          color: 'var(--text)',
        }}
      >
        {val}
        {unit ? (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', marginLeft: 3 }}>{unit}</span>
        ) : null}
      </div>

      <button
        type="button"
        aria-label="Increase"
        onClick={() => set(val + step)}
        disabled={val >= max}
        style={{
          ...btn,
          background: 'var(--brand)',
          color: 'var(--brand-on)',
          opacity: val >= max ? 0.4 : 1,
          cursor: val >= max ? 'not-allowed' : 'pointer',
        }}
      >
        <Icon name="Plus" size={18} strokeWidth={2.5} />
      </button>
    </div>
  );
}
